import React from "react";
import {
  Box,
  Button,
  Grid2 as Grid,
  Grid2Props,
  IconButton,
  Link,
} from "@mui/material";
import {
  AddCircleOutline,
  RemoveCircleOutline,
  OpenInNew,
} from "@mui/icons-material";
import FormTextField from "../../fields/basic/TextField";
import FormSelectField from "../../fields/basic/SelectField";
import FormLabel from "../base/FormLabel";
import { singleGridItem } from "../../fields/defaultStyles";
import ArraySubFormProvider from "../../fields/subForms/array/ArraySubFormProvider";
import ArraySubFormList from "../../fields/subForms/array/ArraySubFormList";
import ArraySubFormAdd from "../../fields/subForms/array/ArraySubFormAdd";
import { useFormHandlerContext } from "../../../../controllers/formController";
import GroupGridContainer from "../../../common/GroupGridContainer";
import { useWatch } from "react-hook-form";
import {
  addProtocolIfMissing,
  isHttpUrl,
  normalizeHttpUrl,
} from "../../../../common/formatters/linkUrls";

export type LinksList = LinkItem[];

export interface LinkItem {
  link: string;
  type: string;
}

const formatLinkItem = (input: LinkItem) => {
  const { link, type } = input;
  if (link) {
    const formattedLink = normalizeHttpUrl(link);
    if (formattedLink) {

      return (
        <Link
          href={formattedLink}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          {type || formattedLink}
        </Link>
      );
    } else {
      return `${type} (${link})`;
    }
  } else {
    return type || "";
  }
};

export const formatLinksList = (input: LinksList) => {
  if (!input || input.length === 0) return "No Links";
  return (
    <Box>
      {input.map((item, index) => (
        <Box key={index}>{formatLinkItem(item)}</Box>
      ))}
    </Box>
  );
};

interface LinksListFieldProps extends Grid2Props {
  name: string;
  label: string;
  rowLabel: string;
  disabled?: boolean;
  valuePlaceHolder?: string | ((index: number) => string);
  typePlaceHolder?: string | ((index: number) => string);
  valueLabel: string | ((index: number) => string);
  typeLabel: string | ((index: number) => string);
  typeEnum: string;
}

const RowLinkPreview: React.FC<{ fieldPath: string }> = ({ fieldPath }) => {
  const value = useWatch({ name: fieldPath }) as string;
  if (!value || !isHttpUrl(value)) return null;

  const url = addProtocolIfMissing(value);

  const handleClick = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <IconButton
      aria-label="Open link"
      onClick={handleClick}
      size="small"
      sx={{
        position: "absolute",
        right: 12,
        top: "67.5%",
        transform: "translateY(-50%)",
      }}
    >
      <OpenInNew fontSize="small" />
    </IconButton>
  );
};

const LinksListField: React.FC<LinksListFieldProps> = ({
  name,
  rowLabel,
  label,
  valueLabel,
  valuePlaceHolder,
  typeLabel,
  typePlaceHolder,
  typeEnum,
  disabled = false,
  ...grid
}) => {
  const { isProcessing } = useFormHandlerContext();
  return (
    <Grid container spacing={1} {...singleGridItem} {...grid}>
      <FormLabel label={label} name={name} />
      <ArraySubFormProvider name={name}>
        <ArraySubFormList
          renderItem={(index, remove) => (
            <GroupGridContainer key={index}>
              <Grid {...singleGridItem}>
                <FormLabel
                  sx={{ fontSize: "1.2rem" }}
                  label={`${rowLabel} ${index + 1}`}
                  actions={
                    <IconButton
                      color="error"
                      onClick={() => remove()}
                      size="small"
                      disabled={disabled || isProcessing}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  }
                />
              </Grid>
              <Box sx={{ position: "relative", width: "100%" }}>
                <FormTextField
                  name="link"
                  label={
                    typeof valueLabel === "function"
                      ? valueLabel(index + 1)
                      : valueLabel
                  }
                  placeholder={
                    typeof valuePlaceHolder === "function"
                      ? valuePlaceHolder(index + 1)
                      : valuePlaceHolder
                  }
                  disabled={disabled || isProcessing}
                  required
                  sx={{ width: "100%", "& .MuiInputBase-input": { pr: 6 } }}
                />
                <RowLinkPreview fieldPath={`${name}.${index}.link`} />
              </Box>
              <FormSelectField
                name="type"
                label={
                  typeof typeLabel === "function"
                    ? typeLabel(index + 1)
                    : typeLabel
                }
                placeholder={
                  typeof typePlaceHolder === "function"
                    ? typePlaceHolder(index + 1)
                    : typePlaceHolder
                }
                useEnum={typeEnum}
                disabled={disabled || isProcessing}
                required
              />
            </GroupGridContainer>
          )}
        />
        <Grid {...singleGridItem}>
          <ArraySubFormAdd
            render={(append) => (
              <Button
                variant="contained"
                fullWidth
                color="primary"
                startIcon={<AddCircleOutline />}
                disabled={disabled || isProcessing}
                onClick={() =>
                  append({
                    link: "",
                    type: null,
                  })
                }
              >
                Add {rowLabel}
              </Button>
            )}
          />
        </Grid>
      </ArraySubFormProvider>
    </Grid>
  );
};

export default LinksListField;
